/**
 * @var AsgVerticals
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { VerticalService } from './src/services/Vertical.service';
import { AsgDictionaryContext, ToolsContext, AsgFwContext, AsgAppContext } from './src/contexts';
import { Provider } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';

/*
  PUBLIC INTERFACE
 */

export const services = {
    VerticalService,
};

// Holds all verticals-related roots.
const rootsMap = new WeakMap();

export const createReactRoot = (element)  => {
    let root = rootsMap.get(element);
    if (!root) {
        root = createRoot(element);
        rootsMap.set(element, root);
    }
    return root;
};

const LogErrorOnly = ({ error }) => {
    Tools.error('Error caught by ErrorBoundary:', error);
    return <></>;
};

export const renderVerticals = async (props) => {
    const { store } = AsgSharedComponents.state;

    let root = createReactRoot(props.element);

    try {
        // Dynamically import the Verticals component only when renderVerticals is called.
        const { Verticals } = await import('./src/contaiters/Verticals');
        root.render(
            <ErrorBoundary fallbackRender={LogErrorOnly}>
                <AsgDictionaryContext.Provider value={Dictionary}>
                    <ToolsContext.Provider value={Tools}>
                        <AsgFwContext.Provider value={AsgFW}>
                            <AsgAppContext.Provider value={AsgApp}>
                                <Provider store={store}>
                                    <Verticals/>
                                </Provider>
                            </AsgAppContext.Provider>
                        </AsgFwContext.Provider>
                    </ToolsContext.Provider>
                </AsgDictionaryContext.Provider>
            </ErrorBoundary>
        );
    } catch (error) {
        Tools.error('Failed to load Verticals component:', error);
    }
};

export const renderHeader = async (props) => {
    const { store } = AsgSharedComponents.state;
    const { app } = store.getState();

    if (app?.header?.globalComponentsVisibility?.header === null) {
        return null;
    }

    let root = createReactRoot(props.element);

    try {
        const { HeaderContainer } = await import('./src/contaiters/HeaderContainer');
        root.render(
            <ErrorBoundary>
                <ToolsContext.Provider value={Tools}>
                    <AsgFwContext.Provider value={AsgFW}>
                        <Provider store={store}>
                            <HeaderContainer {...props} />
                        </Provider>
                    </AsgFwContext.Provider>
                </ToolsContext.Provider>
            </ErrorBoundary>
        );
    } catch (error) {
        Tools.error('Failed to load HeaderContainer component:', error);
    }
};

export const renderSportBanners = async (props) => {
    const { store } = AsgSharedComponents.state;

    let root = createReactRoot(props.element);

    try {
        const { SportBanners } = await import('./src/contaiters/Banners/SportBanners');
        root.render(
            <Provider store={store}>
                <SportBanners/>
            </Provider>
        );
    } catch (error) {
        Tools.error('Failed to load SportBanner component:', error);
    }
};

export const renderEmbeddedGame = async (props) => {
    const { store } = AsgSharedComponents.state;

    let root = createReactRoot(props.element);

    try {
        const { EmbeddedGame } = await import('./src/components/Casino/EmbeddedGame/EmbeddedGame');
        root.render(
            <Provider store={store}>
                <ToolsContext.Provider value={Tools}>
                    <EmbeddedGame />
                </ToolsContext.Provider>
            </Provider>
        );
    } catch (error) {
        Tools.error('Failed to load EmbeddedGame component:', error);
    }
};

/*
  END PUBLIC INTERFACE
 */
